import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { analyticsService } from '../services/analyticsService';
import { generateMonthRange, formatMonthLabel } from '../../../utils/monthRange';

interface Entry { groupName: string; classifierName: string; amount: number; description?: string; date?: string; }
type ViewMode = 'pizza' | 'barras';
type SortDir = 'asc' | 'desc';
interface SortState { key: string; dir: SortDir; }

function useSortedEntries(entries: Entry[], initial: SortState = { key: 'date', dir: 'desc' }) {
  const [sort, setSort] = useState<SortState>(initial);
  const toggle = (key: string) => setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'amount' ? 'desc' : 'asc' });
  const sorted = useMemo(() => {
    const arr = [...entries];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let cmp = 0;
      if (key === 'date') cmp = (a.date || '').localeCompare(b.date || '');
      else if (key === 'description') cmp = (a.description || '').localeCompare(b.description || '');
      else if (key === 'groupName') cmp = a.groupName.localeCompare(b.groupName);
      else if (key === 'classifierName') cmp = a.classifierName.localeCompare(b.classifierName);
      else if (key === 'amount') cmp = a.amount - b.amount;
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [entries, sort]);
  return { sorted, sort, toggle };
}

const SortHeader: React.FC<{ label: string; field: string; sort: SortState; toggle: (k: string) => void; align?: 'left' | 'right' }> = ({ label, field, sort, toggle, align = 'left' }) => {
  let indicator = '';
  if (sort.key === field) indicator = sort.dir === 'asc' ? ' ▲' : ' ▼';
  return (
    <th className={`px-2 py-1.5 font-medium text-gray-600 cursor-pointer select-none hover:text-blue-600 ${align === 'right' ? 'text-right' : 'text-left'}`} onClick={() => toggle(field)}>
      {label}{indicator}
    </th>
  );
};

const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#ca8a04', '#9333ea', '#0891b2', '#e11d48', '#4f46e5', '#059669', '#d97706', '#7c3aed', '#0d9488', '#be123c', '#6366f1', '#047857', '#b45309'];
const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtMonth = formatMonthLabel;
const ALL_MONTHS = generateMonthRange();

// Pie chart label renderer
const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent }: { cx: number; cy: number; midAngle: number; outerRadius: number; percent: number }) => {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const r = outerRadius * 0.75;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>{`${(percent * 100).toFixed(0)}%`}</text>;
};

// Tooltip content for stacked bar charts
const StackedBarTooltip: React.FC<{ active?: boolean; payload?: Array<{ dataKey?: string | number; value?: number; color?: string }>; label?: string }> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  return (
    <div className="bg-white border rounded-lg shadow-lg p-2 text-xs max-w-[250px]">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex justify-between gap-3">
          <span className="truncate" style={{ color: p.color }}>{String(p.dataKey)}</span>
          <span className="shrink-0">{fmtBRL(Number(p.value))}</span>
        </div>
      ))}
      <div className="border-t mt-1 pt-1 flex justify-between font-semibold text-gray-900">
        <span>Total</span>
        <span>{fmtBRL(total)}</span>
      </div>
    </div>
  );
};

// Calendar cell background helper
function getCellBg(isSelected: boolean, hasTotal: boolean, inRange: boolean): string {
  if (isSelected) return 'ring-2 ring-blue-500 bg-blue-50';
  if (hasTotal) return 'bg-red-50';
  if (inRange) return 'bg-gray-100';
  return 'bg-gray-50 opacity-40';
}

function getCalendarDateRange(allDays: string[]) {
  const firstDay = new Date(allDays[0] + 'T12:00:00');
  const lastDay = new Date(allDays[allDays.length - 1] + 'T12:00:00');
  const startDate = new Date(firstDay);
  const dow = startDate.getDay();
  startDate.setDate(startDate.getDate() - (dow === 0 ? 6 : dow - 1));
  const endDate = new Date(lastDay);
  const edow = endDate.getDay();
  if (edow !== 0) endDate.setDate(endDate.getDate() + (7 - edow));
  return { firstDay, lastDay, startDate, endDate };
}

function buildCalendarCells(
  rows: { day: string; total: number }[],
  byDay: Record<string, Entry[]>,
  selectedDay: string | null,
  setSelectedDay: (d: string | null) => void
): React.ReactNode[] | null {
  const dayMap: Record<string, number> = {};
  for (const r of rows) { dayMap[r.day] = r.total; }
  const allDays = Object.keys(byDay).sort((a, b) => a.localeCompare(b));
  if (!allDays.length) return null;
  const { firstDay, lastDay, startDate, endDate } = getCalendarDateRange(allDays);

  const cells: React.ReactNode[] = [];
  const cur = new Date(startDate);
  while (cur <= endDate) {
    const key = cur.toISOString().slice(0, 10);
    const total = dayMap[key];
    const inRange = cur >= firstDay && cur <= lastDay;
    const isSelected = selectedDay === key;
    const bgClass = getCellBg(isSelected, !!total, inRange);
    cells.push(
      <button key={key} type="button" onClick={() => { if (inRange) setSelectedDay(isSelected ? null : key); }}
        className={`rounded p-1 text-center ${bgClass}`}>
        <p className="text-[9px] text-gray-400">{cur.getDate()}</p>
        <p className={`text-[10px] font-semibold ${total ? 'text-red-700' : 'text-gray-300'}`}>{total ? Math.round(total).toLocaleString('pt-BR') : '-'}</p>
      </button>
    );
    cur.setDate(cur.getDate() + 1);
  }
  return cells;
}


const AnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('fin_analytics_view') as ViewMode) || 'pizza';
  });
  const [byMonth, setByMonth] = useState<Record<string, Entry[]>>({});
  const [byDay, setByDay] = useState<Record<string, Entry[]>>({});

  const allMonthKeys = ALL_MONTHS;

  useEffect(() => {
    const saved = localStorage.getItem('fin_analytics_months');
    let initial: string[];
    if (saved) {
      initial = JSON.parse(saved);
    } else {
      const now = new Date();
      initial = [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
    }
    setSelectedMonths(initial);
    analyticsService.getAnalytics(initial).then(res => {
      const d = res.data.data;
      setByMonth(d.byMonth);
      setByDay(d.byDay);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMonths.length || loading) return;
    localStorage.setItem('fin_analytics_months', JSON.stringify(selectedMonths));
    analyticsService.getAnalytics(selectedMonths).then(res => {
      const d = res.data.data;
      setByMonth(d.byMonth);
      setByDay(d.byDay);
    });
  }, [selectedMonths]);

  const toggleMonth = (m: string) => {
    setSelectedMonths(prev => {
      const next = prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m];
      return next;
    });
  };
  const selectAllMonths = () => setSelectedMonths(allMonthKeys);
  const clearMonths = () => setSelectedMonths([]);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Analítico" />
      <div className="px-0 py-4 sm:px-2 space-y-4">
        {/* Month selector */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-600">Competências:</span>
            <button onClick={selectAllMonths} className="text-xs text-blue-600 hover:underline">Todas</button>
            <button onClick={clearMonths} className="text-xs text-blue-600 hover:underline">Nenhuma</button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:flex md:flex-wrap gap-1">
            {allMonthKeys.map(m => (
              <button key={m} onClick={() => toggleMonth(m)}
                className={`text-xs px-2 py-1.5 sm:py-0.5 rounded border text-center ${selectedMonths.includes(m) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                {fmtMonth(m)}
              </button>
            ))}
          </div>
        </div>

        {/* View mode */}
        <select value={viewMode} onChange={e => { const v = e.target.value as ViewMode; setViewMode(v); localStorage.setItem('fin_analytics_view', v); }} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          <option value="pizza">Pizza (% por mês)</option>
          <option value="barras">Barras empilhadas (por dia)</option>
        </select>

        {viewMode === 'pizza' && <PizzaView byMonth={byMonth} selectedMonths={selectedMonths} />}
        {viewMode === 'barras' && <BarrasView byDay={byDay} selectedMonths={selectedMonths} />}
      </div>
    </>
  );
};

// === Pizza View ===
function topNWithOthers(data: { name: string; value: number }[], n = 9): { name: string; value: number }[] {
  if (data.length <= n) return data;
  const top = data.slice(0, n);
  const othersValue = data.slice(n).reduce((s, d) => s + d.value, 0);
  return [...top, { name: 'Outros', value: Math.round(othersValue * 100) / 100 }];
}

const PizzaView: React.FC<{ byMonth: Record<string, Entry[]>; selectedMonths: string[] }> = ({ byMonth, selectedMonths }) => {
  const sortedMonths = [...selectedMonths].sort((a, b) => a.localeCompare(b));
  return (
    <div className="space-y-8">
      {sortedMonths.map(month => {
        const entries = byMonth[month] || [];
        if (!entries.length) return <p key={month} className="text-sm text-gray-500">{fmtMonth(month)}: sem dados</p>;

        const byGroupCls: Record<string, number> = {};
        for (const e of entries) {
          const key = `${e.groupName} / ${e.classifierName}`;
          byGroupCls[key] = (byGroupCls[key] || 0) + e.amount;
        }
        const pieDataFull = topNWithOthers(Object.entries(byGroupCls).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 })).sort((a, b) => b.value - a.value));

        const byGroup: Record<string, number> = {};
        for (const e of entries) {
          byGroup[e.groupName] = (byGroup[e.groupName] || 0) + e.amount;
        }
        const pieDataGroup = topNWithOthers(Object.entries(byGroup).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 })).sort((a, b) => b.value - a.value));

        const total = pieDataGroup.reduce((s, d) => s + d.value, 0);

        return (
          <div key={month}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{fmtMonth(month)} — Total: {fmtBRL(total)}</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <PieChartCard title="Por Agrupador + Classificação" data={pieDataFull} total={total} entries={entries} mode="full" />
              <PieChartCard title="Por Agrupador" data={pieDataGroup} total={total} entries={entries} mode="group" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PieChartCard: React.FC<{ title: string; data: { name: string; value: number }[]; total: number; entries?: Entry[]; mode?: 'full' | 'group' }> = ({ title, data, total, entries = [], mode = 'full' }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const handleClick = (name: string) => {
    setSelectedName(prev => prev === name ? null : name);
  };

  const filteredEntries = useMemo(() => {
    if (!selectedName) return [];
    if (selectedName === 'Outros') {
      const topNames = new Set(data.filter(d => d.name !== 'Outros').map(d => d.name));
      return entries.filter(e => {
        const key = mode === 'full' ? `${e.groupName} / ${e.classifierName}` : e.groupName;
        return !topNames.has(key);
      }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    }
    return entries.filter(e => {
      const key = mode === 'full' ? `${e.groupName} / ${e.classifierName}` : e.groupName;
      return key === selectedName;
    }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [selectedName, entries, mode, data]);

  return (
  <div className="border rounded-lg p-2 sm:p-3">
    <p className="text-xs font-medium text-gray-600 mb-2">{title}</p>
    <ResponsiveContainer width="100%" height={350}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={130} labelLine={false} fontSize={9} isAnimationActive={false}
          onMouseEnter={(_, i) => setActiveIndex(i)}
          onMouseLeave={() => setActiveIndex(null)}
          onClick={(_, i) => handleClick(data[i].name)}
          label={renderPieLabel}
          style={{ cursor: 'pointer' }}>
          {data.map((d, i) => <Cell key={d.name} fill={COLORS[i % COLORS.length]} opacity={activeIndex === null || activeIndex === i ? 1 : 0.4} stroke={activeIndex === i || selectedName === d.name ? '#000' : 'none'} strokeWidth={activeIndex === i || selectedName === d.name ? 2 : 0} />)}
        </Pie>
        <Tooltip formatter={(v: number) => fmtBRL(v)} />
      </PieChart>
    </ResponsiveContainer>
    <div className="mt-2 mx-auto max-w-sm">
      <table className="w-full text-xs">
        <tbody>
          {data.map((d, i) => {
            const isSelected = selectedName === d.name;
            let rowClass = 'transition-colors cursor-pointer';
            if (isSelected) rowClass += ' bg-blue-100 font-semibold';
            else if (activeIndex === i) rowClass += ' bg-blue-50 font-semibold';
            else if (activeIndex !== null) rowClass += ' opacity-50';
            return (
            <tr key={d.name} className={rowClass}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() => handleClick(d.name)}>
              <td className="px-1 py-0.5"><span className="flex items-center gap-1.5 truncate"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.name}</span></td>
              <td className="px-1 py-0.5 text-right text-gray-600 whitespace-nowrap">{fmtBRL(d.value)}</td>
              <td className="px-1 py-0.5 text-right text-gray-500 whitespace-nowrap">{(d.value / total * 100).toFixed(1)}%</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {selectedName && filteredEntries.length > 0 && (
      <PieChartDetailTable entries={filteredEntries} selectedName={selectedName} onClose={() => setSelectedName(null)} />
    )}
  </div>
  );
};

const PieChartDetailTable: React.FC<{ entries: Entry[]; selectedName: string; onClose: () => void }> = ({ entries, selectedName, onClose }) => {
  const { sorted, sort, toggle } = useSortedEntries(entries);
  return (
    <div className="mt-3 border-t pt-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-gray-600">{selectedName} — {entries.length} transações</p>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
      </div>
      {/* Mobile cards */}
      <div className="sm:hidden max-h-96 overflow-y-auto divide-y">
        {sorted.map((e, i) => (
          <div key={`${e.description}-${i}`} className="px-1 py-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-800 truncate flex-1">{e.description || `${e.groupName} / ${e.classifierName}`}</span>
              <span className="text-xs font-semibold text-red-700 shrink-0 ml-2">{fmtBRL(e.amount)}</span>
            </div>
            <div className="flex gap-2 mt-0.5 text-[10px] text-gray-500">
              {e.date && <span>{new Date(e.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} {new Date(e.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}</span>}
              <span>📂 {e.groupName}</span>
              <span>🏷️ {e.classifierName}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Desktop table */}
      <div className="hidden sm:block max-h-96 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b sticky top-0">
            <tr>
              <SortHeader label="Data" field="date" sort={sort} toggle={toggle} />
              <SortHeader label="Descrição" field="description" sort={sort} toggle={toggle} />
              <SortHeader label="Agrupador" field="groupName" sort={sort} toggle={toggle} />
              <SortHeader label="Classificação" field="classifierName" sort={sort} toggle={toggle} />
              <SortHeader label="Valor" field="amount" sort={sort} toggle={toggle} align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, i) => (
              <tr key={`${e.description}-${i}`} className="border-b hover:bg-gray-50">
                <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{e.date ? `${new Date(e.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} ${new Date(e.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}` : ''}</td>
                <td className="px-2 py-1.5 text-gray-800">{e.description || `${e.groupName} / ${e.classifierName}`}</td>
                <td className="px-2 py-1.5 text-gray-600">{e.groupName}</td>
                <td className="px-2 py-1.5 text-gray-600">{e.classifierName}</td>
                <td className="px-2 py-1.5 text-right font-semibold text-red-700">{fmtBRL(e.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// === Bar Chart View ===
const BarrasView: React.FC<{ byDay: Record<string, Entry[]>; selectedMonths: string[] }> = ({ byDay, selectedMonths }) => {
  const sortedMonths = [...selectedMonths].sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-8">
      {sortedMonths.map(month => {
        // Filter days for this month
        const monthDays: Record<string, Entry[]> = {};
        for (const [day, entries] of Object.entries(byDay)) {
          if (day.startsWith(month)) monthDays[day] = entries;
        }
        if (!Object.keys(monthDays).length) return <p key={month} className="text-sm text-gray-500">{fmtMonth(month)}: sem dados</p>;

        return <BarrasMonthSection key={month} month={month} byDay={monthDays} />;
      })}
    </div>
  );
};

const BarrasMonthSection: React.FC<{ month: string; byDay: Record<string, Entry[]> }> = ({ month, byDay }) => {
  const { chartData, keys, keyColors } = useMemo(() => {
    const allKeys = new Set<string>();
    const dayMap: Record<string, Record<string, number>> = {};

    for (const [day, entries] of Object.entries(byDay)) {
      dayMap[day] = {};
      for (const e of entries) {
        const key = `${e.groupName} / ${e.classifierName}`;
        allKeys.add(key);
        dayMap[day][key] = (dayMap[day][key] || 0) + e.amount;
      }
    }

    const sortedDays = Object.keys(dayMap).sort((a, b) => a.localeCompare(b));
    const sortedKeys = Array.from(allKeys).sort((a, b) => a.localeCompare(b));
    const colors: Record<string, string> = {};
    sortedKeys.forEach((k, i) => { colors[k] = COLORS[i % COLORS.length]; });

    const data = sortedDays.map(day => ({ day: day.slice(5), ...dayMap[day] }));
    return { chartData: data, keys: sortedKeys, keyColors: colors };
  }, [byDay]);

  const { chartDataGroup, groupKeys, groupColors } = useMemo(() => {
    const allKeys = new Set<string>();
    const dayMap: Record<string, Record<string, number>> = {};

    for (const [day, entries] of Object.entries(byDay)) {
      dayMap[day] = {};
      for (const e of entries) {
        allKeys.add(e.groupName);
        dayMap[day][e.groupName] = (dayMap[day][e.groupName] || 0) + e.amount;
      }
    }

    const sortedDays = Object.keys(dayMap).sort((a, b) => a.localeCompare(b));
    const sortedKeys = Array.from(allKeys).sort((a, b) => a.localeCompare(b));
    const colors: Record<string, string> = {};
    sortedKeys.forEach((k, i) => { colors[k] = COLORS[i % COLORS.length]; });

    const data = sortedDays.map(day => ({ day: day.slice(5), ...dayMap[day] }));
    return { chartDataGroup: data, groupKeys: sortedKeys, groupColors: colors };
  }, [byDay]);

  const dailyTotals = useMemo(() => {
    const totals: { day: string; total: number }[] = [];
    for (const [day, entries] of Object.entries(byDay)) {
      totals.push({ day, total: entries.reduce((s, e) => s + e.amount, 0) });
    }
    return totals.sort((a, b) => a.day.localeCompare(b.day));
  }, [byDay]);

  const stats = useMemo(() => {
    if (!dailyTotals.length) return null;
    const totalGeral = dailyTotals.reduce((s, d) => s + d.total, 0);
    const media = totalGeral / dailyTotals.length;
    const max = dailyTotals.reduce((m, d) => d.total > m.total ? d : m, dailyTotals[0]);
    const min = dailyTotals.reduce((m, d) => d.total < m.total ? d : m, dailyTotals[0]);
    const daysWithExpense = dailyTotals.filter(d => d.total > 0).length;
    const avgDaysWithExpense = daysWithExpense ? totalGeral / daysWithExpense : 0;
    return { totalGeral, media, max, min, daysWithExpense, daysWithoutExpense: dailyTotals.length - daysWithExpense, avgDaysWithExpense };
  }, [dailyTotals]);

  const [barFilterFull, setBarFilterFull] = useState<string | null>(null);
  const [barFilterGroup, setBarFilterGroup] = useState<string | null>(null);

  if (!chartData.length) return <p className="text-sm text-gray-500">{fmtMonth(month)}: sem dados</p>;

  const handleBarClickFull = (daySlice: string) => {
    const fullDay = `${month.slice(0, 4)}-${daySlice}`;
    setBarFilterFull(prev => prev === fullDay ? null : fullDay);
  };

  const handleBarClickGroup = (daySlice: string) => {
    const fullDay = `${month.slice(0, 4)}-${daySlice}`;
    setBarFilterGroup(prev => prev === fullDay ? null : fullDay);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-800">{fmtMonth(month)} — Total: {fmtBRL(stats?.totalGeral || 0)}</h3>
      <StackedBarCard title="Por Agrupador + Classificação (por dia)" data={chartData} keys={keys} colors={keyColors} onBarClick={handleBarClickFull} />
      <DailyTable byDay={byDay} mode="full" externalFilter={barFilterFull} />
      <StackedBarCard title="Por Agrupador (por dia)" data={chartDataGroup} keys={groupKeys} colors={groupColors} onBarClick={handleBarClickGroup} />
      <DailyTable byDay={byDay} mode="group" externalFilter={barFilterGroup} />

      {stats && (
        <div className="border rounded-lg p-2 sm:p-3">
          <p className="text-xs font-medium text-gray-600 mb-2">Resumo do período</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-red-50 rounded-lg p-2">
              <p className="text-[10px] text-red-600">Total gasto</p>
              <p className="text-sm font-bold text-red-700">{fmtBRL(stats.totalGeral)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-2">
              <p className="text-[10px] text-blue-600">Média diária</p>
              <p className="text-sm font-bold text-blue-700">{fmtBRL(stats.media)}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-2">
              <p className="text-[10px] text-orange-600">Maior gasto ({new Date(stats.max.day + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})</p>
              <p className="text-sm font-bold text-orange-700">{fmtBRL(stats.max.total)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <p className="text-[10px] text-green-600">Menor gasto ({new Date(stats.min.day + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})</p>
              <p className="text-sm font-bold text-green-700">{fmtBRL(stats.min.total)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Dias com gasto</p>
              <p className="text-sm font-bold text-gray-800">{stats.daysWithExpense}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-2">
              <p className="text-[10px] text-purple-600">Média (dias c/ gasto)</p>
              <p className="text-sm font-bold text-purple-700">{fmtBRL(stats.avgDaysWithExpense)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DailyTable: React.FC<{ byDay: Record<string, Entry[]>; mode: 'full' | 'group'; externalFilter?: string | null }> = ({ byDay, mode, externalFilter }) => {
  const [filterKeys, setFilterKeys] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    if (externalFilter) setSelectedDay(externalFilter);
  }, [externalFilter]);

  const allKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const entries of Object.values(byDay)) {
      for (const e of entries) {
        keys.add(mode === 'full' ? `${e.groupName} / ${e.classifierName}` : e.groupName);
      }
    }
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [byDay, mode]);

  const filteredOptions = useMemo(() => {
    if (!search) return allKeys;
    return allKeys.filter(k => k.toLowerCase().includes(search.toLowerCase()));
  }, [allKeys, search]);

  const rows = useMemo(() => {
    const sortedDays = Object.keys(byDay).sort((a, b) => a.localeCompare(b));
    return sortedDays.map(day => {
      const entries = byDay[day].filter(e => {
        if (!filterKeys.size) return true;
        const key = mode === 'full' ? `${e.groupName} / ${e.classifierName}` : e.groupName;
        return filterKeys.has(key);
      });
      return { day, total: entries.reduce((s, e) => s + e.amount, 0) };
    }).filter(r => r.total > 0);
  }, [byDay, mode, filterKeys]);

  const toggleFilter = (k: string) => setFilterKeys(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  return (
    <div className="border rounded-lg p-2 sm:p-3 space-y-2">
      {/* Multi-select combobox */}
      <div className="relative">
        <div className="flex gap-1">
          <div className="flex-1 relative">
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              placeholder={filterKeys.size ? `${filterKeys.size} selecionado(s)` : 'Filtrar categorias...'}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {dropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredOptions.map(k => (
                  <label key={k} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={filterKeys.has(k)} onChange={() => toggleFilter(k)} className="rounded w-3 h-3" />
                    <span className="truncate">{k}</span>
                  </label>
                ))}
                {filteredOptions.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Nenhum resultado</p>}
              </div>
            )}
          </div>
          <button onClick={() => { setFilterKeys(new Set(allKeys)); }} className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap">Todos</button>
          <button onClick={() => { setFilterKeys(new Set()); setSearch(''); }} className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-red-600 whitespace-nowrap">Limpar</button>
        </div>
        {dropdownOpen && <button type="button" aria-label="Fechar dropdown" className="fixed inset-0 z-10 cursor-default" onClick={() => setDropdownOpen(false)} />}
      </div>

      {/* Weekly calendar */}
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map(d => (
            <p key={d} className="text-[10px] text-gray-500 text-center font-medium">{d}</p>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {buildCalendarCells(rows, byDay, selectedDay, setSelectedDay)}
        </div>
      </div>

      {/* Transactions for selected day */}
      {selectedDay && <DayDetail byDay={byDay} selectedDay={selectedDay} setSelectedDay={setSelectedDay} filterKeys={filterKeys} mode={mode} />}
    </div>
  );
};

const DayDetail: React.FC<{ byDay: Record<string, Entry[]>; selectedDay: string; setSelectedDay: (d: string | null) => void; filterKeys: Set<string>; mode: 'full' | 'group' }> = ({ byDay, selectedDay, setSelectedDay, filterKeys, mode }) => {
  const dayEntries = useMemo(() => [...(byDay[selectedDay] || [])].filter(e => {
    if (!filterKeys.size) return true;
    const key = mode === 'full' ? `${e.groupName} / ${e.classifierName}` : e.groupName;
    return filterKeys.has(key);
  }), [byDay, selectedDay, filterKeys, mode]);

  const { sorted, sort, toggle } = useSortedEntries(dayEntries);

  if (!dayEntries.length) return (
    <div className="border rounded-lg p-3 text-xs text-gray-500 text-center">
      Nenhuma transação em {new Date(selectedDay + 'T12:00:00').toLocaleDateString('pt-BR')}
      <button onClick={() => setSelectedDay(null)} className="ml-2 text-gray-400 hover:text-gray-600">✕</button>
    </div>
  );
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-3 py-1.5 border-b flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Transações de {new Date(selectedDay + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
        <button onClick={() => setSelectedDay(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
      </div>
      {/* Mobile cards */}
      <div className="sm:hidden divide-y">
        {sorted.map((e, i) => (
            <div key={`${e.description}-${i}`} className="px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-800 truncate flex-1">{e.description || `${e.groupName} / ${e.classifierName}`}</span>
                <span className="text-xs font-semibold text-red-700 shrink-0 ml-2">{fmtBRL(e.amount)}</span>
              </div>
              <div className="flex gap-2 mt-0.5 text-[10px] text-gray-500">
                {e.date && <span>{new Date(e.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}</span>}
                <span>📂 {e.groupName}</span>
                <span>🏷️ {e.classifierName}</span>
              </div>
            </div>
        ))}
      </div>
      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortHeader label="Hora" field="date" sort={sort} toggle={toggle} />
              <SortHeader label="Descrição" field="description" sort={sort} toggle={toggle} />
              <SortHeader label="Agrupador" field="groupName" sort={sort} toggle={toggle} />
              <SortHeader label="Classificação" field="classifierName" sort={sort} toggle={toggle} />
              <SortHeader label="Valor" field="amount" sort={sort} toggle={toggle} align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, i) => (
                <tr key={`${e.description}-${i}`} className="border-b hover:bg-gray-50">
                  <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{e.date ? new Date(e.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : ''}</td>
                  <td className="px-2 py-1.5 text-gray-800">{e.description || `${e.groupName} / ${e.classifierName}`}</td>
                  <td className="px-2 py-1.5 text-gray-600">{e.groupName}</td>
                  <td className="px-2 py-1.5 text-gray-600">{e.classifierName}</td>
                  <td className="px-2 py-1.5 text-right font-semibold text-red-700">{fmtBRL(e.amount)}</td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StackedBarCard: React.FC<{ title: string; data: Record<string, string | number>[]; keys: string[]; colors: Record<string, string>; onBarClick?: (day: string) => void }> = ({ title, data, keys, colors, onBarClick }) => (
  <div className="border rounded-lg p-2 sm:p-3">
    <p className="text-xs font-medium text-gray-600 mb-2">{title}</p>
    {/* Mobile: horizontal bars (vertical layout) */}
    <div className="sm:hidden">
      <ResponsiveContainer width="100%" height={data.length * 28 + 40}>
        <BarChart data={data} layout="vertical" barSize={14}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" fontSize={9} tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
          <YAxis type="category" dataKey="day" fontSize={9} width={30} interval={0} tickFormatter={(v: string) => v.split('-')[1]} />
          <Tooltip content={<StackedBarTooltip />} />
          {keys.map(k => <Bar key={k} dataKey={k} stackId="a" fill={colors[k]} onClick={onBarClick ? (entry: any) => onBarClick(entry.day) : undefined} style={onBarClick ? { cursor: 'pointer' } : undefined} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
    {/* Desktop: vertical bars */}
    <div className="hidden sm:block">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} barSize={16}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="day" fontSize={9} interval={0} tickLine={false} tickFormatter={(v: string) => v.split('-')[1]} />
          <YAxis fontSize={10} tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
          <Tooltip content={<StackedBarTooltip />} />
          {keys.map(k => <Bar key={k} dataKey={k} stackId="a" fill={colors[k]} onClick={onBarClick ? (entry: any) => onBarClick(entry.day) : undefined} style={onBarClick ? { cursor: 'pointer' } : undefined} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export default AnalyticsPage;
